import { appendShareToken, getShareToken } from './share.js';

export const getApiErrorMessage = (
  error,
  fallback = 'Something went wrong.'
) => {
  if (!error || typeof error !== 'object') {
    return fallback;
  }

  if (
    error.payload
    && typeof error.payload.message === 'string'
    && error.payload.message.trim()
  ) {
    return error.payload.message;
  }

  if (typeof error.body === 'string' && error.body.trim()) {
    try {
      const parsed = JSON.parse(error.body);
      if (
        parsed
        && typeof parsed.message === 'string'
        && parsed.message.trim()
      ) {
        return parsed.message;
      }
    } catch (_err) {
      return error.body.trim();
    }
  }

  if (typeof error.message === 'string' && error.message.trim()) {
    if (!error.message.startsWith('HTTP error! status:')) {
      return error.message;
    }
  }

  return fallback;
};

// Abort rejections are not always named "AbortError": Firefox uses a
// DOMException with code 20, and rejections raised after the abort
// (e.g. while reading the body) can surface as plain TypeErrors. The
// signal itself is the authoritative source, so callers should pass
// it when they have one.
export const isAbortError = (err, signal = null) => (
  Boolean(signal?.aborted)
  || err?.name === 'AbortError'
  || err?.code === 20
  || err?.message === 'The operation was aborted.'
);

export const apiFetch = async (url, options = {}) => {
  const {
    signal,
    parseJson = true,
    allowNonOk = false,
    ...fetchOptions
  } = options;
  const response = await fetch(
    appendShareToken(url),
    { signal, ...fetchOptions }
  );

  if (response.status === 403) {
    if (!getShareToken()) {
      window.location.href = '/login';
    }
    return null;
  }

  if (!response.ok && !allowNonOk) {
    let body = '';
    let payload = null;
    try {
      body = await response.text();
    } catch (_err) {
      body = '';
    }

    if (body) {
      try {
        payload = JSON.parse(body);
      } catch (_err) {
        payload = null;
      }
    }

    const message = payload?.message
      ? payload.message
      : `HTTP error! status: ${response.status}`;
    const error = new Error(message);
    error.status = response.status;
    error.body = body;
    error.payload = payload;
    throw error;
  }

  if (!parseJson) {
    return response;
  }

  try {
    return await response.json();
  } catch (err) {
    err.status = response.status;
    throw err;
  }
};

// Network-level failures (fetch rejecting with a TypeError and no
// HTTP status) are frequently transient: the dev server drops
// concurrent slow requests with an empty response, wifi blips, etc.
// HTTP errors are authoritative answers and aborts mean the caller
// no longer wants the data, so neither is retried.
const isTransientNetworkError = (err, signal) => (
  err instanceof TypeError
  && typeof err.status === 'undefined'
  && !isAbortError(err, signal)
);

export const apiFetchWithRetry = async (url, options = {}, retries = 2) => {
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await apiFetch(url, options);
    } catch (err) {
      if (attempt >= retries || !isTransientNetworkError(err, options.signal)) {
        throw err;
      }
      // Linear backoff gives a momentarily-overloaded server room to
      // recover without keeping the user waiting for long.
      await new Promise(resolve => (
        setTimeout(resolve, 500 * (attempt + 1))
      ));
      if (options.signal?.aborted) {
        throw err;
      }
    }
  }
};
